import { createSelector } from '@reduxjs/toolkit';
import { ApplicationState } from 'clients/app/src/rootReducer';

import { EditQuizStatus } from './EditQuizStatus';

const selectQuizError = (state: ApplicationState) =>
  state.teachers.quizData.error;

const selectEditingQuiz = (state: ApplicationState) =>
  state.teachers.quizData.editQuiz;

const selectIsSaving = (state: ApplicationState) =>
  state.teachers.quizData.status === EditQuizStatus.Saving;

const selectQuizById = (state: ApplicationState, props: { quizId: string }) =>
  state.teachers.quizData.quizzes[props.quizId];

const selectQuizBySocNumber = (
  state: ApplicationState,
  props: { socNumber: string }
) =>
  Object.values(state.teachers.quizData.quizzes).find(
    (quiz) => quiz.socNumber === props.socNumber
  );

const selectEditAnswers = (state: ApplicationState) =>
  state.teachers.quizData.editAnswers;

const selectAnswers = (state: ApplicationState) =>
  state.teachers.quizData.answers;

const selectIsEditing = (state: ApplicationState) =>
  state.teachers.quizData.editQuestion !== null;

const selectQuestion = (
  state: ApplicationState,
  props: { questionId: string }
) => state.teachers.quizData.questions[props.questionId];

const selectEditQuestion = (state: ApplicationState) =>
  state.teachers.quizData.editQuestion;

function selectOrderedQuestions(
  state: ApplicationState,
  props: { questionIds: Array<number | string> }
) {
  return (props.questionIds ?? [])
    .map((id) => {
      return typeof id === 'number'
        ? state.teachers.quizData.questions[`${id}`]
        : state.teachers.quizData.editQuestion;
    })
    .filter(Boolean)
    .map(({ order, questionId, temporaryOrder, gradingWeight }) => {
      return {
        order: temporaryOrder ?? order,
        questionId: questionId,
        gradingWeight: gradingWeight,
      };
    })
    .sort((a, b) => {
      const diff = a.order - b.order;
      if (diff === 0) {
        // If the orders match, put the editing question first
        return typeof a.questionId === 'string' ? -1 : 1;
      }
      return diff;
    });
}

const selectQuizStatus = (state: ApplicationState) =>
  state.teachers.quizData.status;

const makeSelectIsQuizDirty = () =>
  createSelector([selectEditingQuiz], (editingQuiz) => {
    if (editingQuiz == null) return false;
    return editingQuiz.isDirty;
  });

const makeSelectIsQuestionDirty = () =>
  createSelector([selectEditQuestion], (editQuestion) => {
    if (editQuestion == null) return false;
    return editQuestion.isDirty;
  });

/**
 * @deprecated
 */
const makeSelectIsEditingThisQuestion = () =>
  createSelector(
    [
      selectEditQuestion,
      (_state: ApplicationState, props: { questionId: string }) =>
        props.questionId,
    ],
    (editQuestion, questionId) => {
      if (!editQuestion) return false;

      return editQuestion.questionId === questionId;
    }
  );

const makeSelectEditAnswers = () =>
  createSelector([selectEditAnswers], (editAnswers) => {
    return editAnswers;
  });

const makeSelectAnswers = () =>
  createSelector([selectAnswers], (quizAnswers) => {
    return quizAnswers;
  });

const makeSelectIsControlsDisabled = () =>
  createSelector([selectQuizStatus], (quizStatus: EditQuizStatus) => {
    switch (quizStatus) {
      case EditQuizStatus.Ok: {
        return false;
      }
      case EditQuizStatus.Loading:
      case EditQuizStatus.Saving:
      case EditQuizStatus.Error: {
        return true;
      }
      default:
        throw new Error('unhandled EditQuizStatus');
    }
  });

/**
 * @deprecated
 */
const makeSelectEditableQuestion = () =>
  createSelector(
    [makeSelectIsEditingThisQuestion(), selectQuestion, selectEditQuestion],
    (isEditingThisQuestion, storeQuestion, editQuestion) => {
      return isEditingThisQuestion ? editQuestion : storeQuestion;
    }
  );

/**
 * @deprecated
 */
const makeSelectQuestionType = () =>
  createSelector([makeSelectEditableQuestion()], (question) => {
    return question ? question.type : null;
  });

export {
  selectQuizError,
  selectEditingQuiz,
  selectIsSaving,
  selectQuizById,
  selectQuizBySocNumber,
  selectEditAnswers,
  selectAnswers,
  selectIsEditing,
  selectQuestion,
  selectEditQuestion,
  selectOrderedQuestions,
  selectQuizStatus,
  makeSelectIsQuizDirty,
  makeSelectIsQuestionDirty,
  makeSelectIsControlsDisabled,
  makeSelectIsEditingThisQuestion,
  makeSelectEditableQuestion,
  makeSelectQuestionType,
  makeSelectEditAnswers,
  makeSelectAnswers,
};
